import { useQuery } from '@tanstack/react-query';
import {
  getProductsBySellerId,
  getRelatedProducts,
} from '@util/firestore/products';
import {
  getProductsByActivityServer,
  getYouMayAlsoLikeServer,
} from '@util/firestore/products/products.typesense';
import { useAuth } from 'context/AuthContext';
import { ProductDocument } from 'models/product';
import { isBot } from 'next/dist/server/web/spec-extension/user-agent';
import useUserProfile from './useUserProfile';

export default function useSuggestedProducts({
  product,
}: {
  product: ProductDocument;
}) {
  const { userDoc } = useAuth();
  const {
    data: { data: userProfile },
  } = useUserProfile();

  const { data: basedOnActivity } = useQuery({
    queryKey: ['basedOnActivity', userProfile?.uid, product.id],
    queryFn: async () => {
      const vector = userProfile?.activity_profile?.toArray() ?? [];
      return getProductsByActivityServer(product, vector);
    },
    enabled: !!userProfile?.uid,
    throwOnError: false,
  });
  const { data, isLoading } = useQuery({
    queryKey: ['relatedProducts', product.id],
    queryFn: () => getRelatedProducts(product, !userDoc ? 30 : 20),
    enabled: true,
    throwOnError: false,
  });
  const relatedProducts = data?.results;

  const { data: moreProductsFromSeller } = useQuery({
    queryKey: ['moreProductsFromSeller', product.seller_id],
    queryFn: () =>
      getProductsBySellerId(product.seller_id ?? '', 10, product.id),
    enabled: !!product.seller_id,
    throwOnError: false,
  });
  const youMayAlsoLike = useQuery({
    queryKey: [
      'youMayAlsoLike',
      userDoc?.uid,
      product.id,
      relatedProducts?.map((p) => p.id),
    ],
    queryFn: async () =>
      getYouMayAlsoLikeServer({
        product,
        excludeIds: relatedProducts?.map((p) => p.id) ?? [],
        userBikes: userDoc?.bikes,
      }),
    enabled:
      !!product.id &&
      !!userDoc?.uid &&
      typeof relatedProducts !== 'undefined' &&
      !isBot(window.navigator.userAgent),
  });

  return {
    basedOnActivity,
    youMayAlsoLike,
    relatedProducts,
    moreProductsFromSeller,
    isLoading,
  };
}
